import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { C, ff, t } from '../../../shared/constants/dashboardConstants';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import { getCastingDetail, updateCasting, changeCastingStatus } from '../../../api/castingApi';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

const GENRES = ['드라마', '영화', '웹드라마', 'CF·광고', '뮤지컬', '독립영화', '예능', '기타'];
const PLATFORMS = ['Netflix', 'tvN', 'MBC', 'KBS', 'SBS', 'OCN', 'JTBC', 'Wavve', 'Tving', '기타'];
const ROLE_TYPES = ['주연', '조연', '단역', '특별출연'];

const METRO_CITIES = ['인천', '부산', '대구', '대전', '광주', '울산', '세종'];
const REGION_DATA = [
  { province: '서울', cities: [] },
  { province: '광역시', cities: METRO_CITIES },
  { province: '경기', cities: ['수원', '성남', '고양', '용인', '부천', '안산', '안양', '남양주', '화성', '평택', '의정부', '시흥', '파주', '광명', '김포', '군포', '광주', '이천', '양주', '오산', '구리', '안성', '포천', '의왕', '하남', '여주', '동두천', '과천'] },
  { province: '강원', cities: ['춘천', '원주', '강릉', '동해', '태백', '속초', '삼척'] },
  { province: '충북', cities: ['청주', '충주', '제천'] },
  { province: '충남', cities: ['천안', '공주', '보령', '아산', '서산', '논산', '계룡', '당진'] },
  { province: '전북', cities: ['전주', '군산', '익산', '정읍', '남원', '김제'] },
  { province: '전남', cities: ['목포', '여수', '순천', '나주', '광양'] },
  { province: '경북', cities: ['포항', '경주', '김천', '안동', '구미', '영주', '영천', '상주', '문경', '경산'] },
  { province: '경남', cities: ['창원', '진주', '통영', '사천', '김해', '밀양', '거제', '양산'] },
  { province: '제주', cities: [] },
];
const DEFAULT_STAGE_NAMES = ['서류 심사', '1차 선발', '2차 선발', '최종 선발'];

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i - 2);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

const GENDER_API_TO_FORM = { MALE: '남성', FEMALE: '여성', ANY: '전체' };

function SectionCard({ title, children }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '20px', padding: '30px', marginBottom: '20px' }}>
      <h2 style={{ ...t('20px', 700, C.bk), margin: '0 0 24px 0' }}>{title}</h2>
      {children}
    </div>
  );
}

function FormRow({ label, required, children, extra }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
        <label style={t('14px', 600, C.bk)}>
          {label} {required && <span style={{ color: C.orange }}>*</span>}
        </label>
        {extra}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', height: '46px', padding: '0 16px', border: `1px solid ${C.g200}`,
  borderRadius: '10px', fontFamily: ff, fontSize: '15px', outline: 'none', boxSizing: 'border-box',
};

const textareaStyle = {
  ...inputStyle, height: 'auto', resize: 'vertical', minHeight: '100px', padding: '14px 16px',
};

function YearMonthPicker({ year, month, onYearChange, onMonthChange }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <select value={year} onChange={e => onYearChange(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
        <option value="">연도 선택</option>
        {YEARS.map(y => <option key={y} value={y}>{y}년</option>)}
      </select>
      <select value={month} onChange={e => onMonthChange(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
        <option value="">월 선택</option>
        {MONTHS.map(m => <option key={m} value={m}>{m}월</option>)}
      </select>
    </div>
  );
}

function TbdButton({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
        border: `1px solid ${active ? C.bk : C.g200}`,
        background: active ? C.bk : C.white,
        color: active ? C.white : C.sub,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      미정
    </button>
  );
}

function LocationPicker({ value, onChange }) {
  const [province, setProvince] = useState(() => {
    if (!value) return '';
    const r = REGION_DATA.find(r => r.province === value || r.cities.includes(value));
    return r ? r.province : '';
  });

  // API 데이터 로드 후 value가 채워지면 province 동기화
  const prevValue = useRef(value);
  useEffect(() => {
    if (prevValue.current !== value) {
      prevValue.current = value;
      if (!value) { setProvince(''); return; }
      const r = REGION_DATA.find(r => r.province === value || r.cities.includes(value));
      setProvince(r ? r.province : '');
    }
  }, [value]);

  const handleProvinceChange = (p) => {
    setProvince(p);
    onChange(p);
  };

  const cities = REGION_DATA.find(r => r.province === province)?.cities || [];

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <select
        value={province}
        onChange={e => handleProvinceChange(e.target.value)}
        style={{ ...inputStyle, flex: 1 }}
      >
        <option value="">시/도 선택</option>
        {REGION_DATA.map(r => (
          <option key={r.province} value={r.province}>{r.province === '광역시' ? '광역시 / 특별시' : r.province}</option>
        ))}
      </select>
      {cities.length > 0 && (
        <select
          value={cities.includes(value) ? value : ''}
          onChange={e => onChange(e.target.value || province)}
          style={{ ...inputStyle, flex: 1 }}
        >
          <option value="">전체 {province}</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  );
}

function RoleCardReadOnly({ role, index }) {
  const genderLabel = role.gender === 'ANY' ? '전체' : role.gender === 'MALE' ? '남성' : role.gender === 'FEMALE' ? '여성' : role.gender || '전체';
  const ageLabel = role.ageMin || role.ageMax ? `${role.ageMin || '?'} ~ ${role.ageMax || '?'}세` : '제한 없음';
  return (
    <div style={{ border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px', background: '#fafafa', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <span style={t('16px', 700, C.bk)}>역할 {index + 1}</span>
        <span style={{ padding: '4px 10px', borderRadius: '8px', background: C.g200, ...t('12px', 500, C.sub) }}>{role.type || '주연'}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>역할명</div>
          <div style={{ ...t('14px', 500, C.bk), padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white }}>{role.name || '—'}</div>
        </div>
        <div>
          <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>성별</div>
          <div style={{ ...t('14px', 500, C.bk), padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white }}>{genderLabel}</div>
        </div>
        <div>
          <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>나이 범위</div>
          <div style={{ ...t('14px', 500, C.bk), padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white }}>{ageLabel}</div>
        </div>
        <div>
          <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>출연료</div>
          <div style={{ ...t('14px', 500, C.bk), padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white }}>{role.pay || '협의 후 결정'}</div>
        </div>
        {role.desc && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ ...t('12px', 400, C.sub), marginBottom: '4px' }}>역할 설명</div>
            <div style={{ ...t('14px', 400, C.bk), padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: '10px', background: C.white, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{role.desc}</div>
          </div>
        )}
      </div>
    </div>
  );
}

const defaultRole = () => ({ roleId: null, name: '', type: '주연', gender: '전체', ageMin: '', ageMax: '', pay: '', desc: '' });

function RoleCard({ role, index, onChange, onRemove }) {
  return (
    <div style={{ border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <span style={t('16px', 700, C.bk)}>역할 {index + 1}{index === 0 && <span style={{ color: '#F97316', marginLeft: '4px' }}>*</span>}</span>
        {index > 0 && (
          <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', ...t('14px', 500, '#EF4444') }}>삭제</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormRow label="역할명">
          <input value={role.name} onChange={e => onChange('name', e.target.value)} placeholder="예: 주인공 김민준" style={inputStyle} />
        </FormRow>
        <FormRow label="역할 유형">
          <select value={role.type} onChange={e => onChange('type', e.target.value)} style={inputStyle}>
            {['주연', '조연', '단역', '특별출연'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </FormRow>
        <FormRow label="성별">
          <div style={{ display: 'flex', gap: '8px' }}>
            {['전체', '남성', '여성'].map(g => (
              <button key={g} onClick={() => onChange('gender', g)}
                style={{ flex: 1, padding: '10px', borderRadius: '10px', border: `1px solid ${role.gender === g ? C.bk : C.g200}`, background: role.gender === g ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, role.gender === g ? C.white : C.bk) }}>
                {g}
              </button>
            ))}
          </div>
        </FormRow>
        <FormRow label="나이 범위">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="number" min="0" max="100" value={role.ageMin} onChange={e => onChange('ageMin', Math.min(100, Math.max(0, e.target.value)))} placeholder="최소" style={{ ...inputStyle, flex: 1 }} />
            <span style={t('14px', 400, C.sub)}>~</span>
            <input type="number" min="0" max="100" value={role.ageMax} onChange={e => onChange('ageMax', Math.min(100, Math.max(0, e.target.value)))} placeholder="최대" style={{ ...inputStyle, flex: 1 }} />
          </div>
        </FormRow>
        <FormRow label="출연료">
          <input value={role.pay} onChange={e => onChange('pay', e.target.value)} placeholder="예: 40만원 / 10만원 (협의 시 비워두세요)" style={inputStyle} />
        </FormRow>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormRow label="역할 설명">
            <textarea value={role.desc} onChange={e => onChange('desc', e.target.value)} placeholder="역할의 성격, 특징, 요구 사항을 작성해 주세요." style={{ ...inputStyle, height: 'auto', resize: 'vertical', minHeight: '100px', padding: '14px 16px' }} />
          </FormRow>
        </div>
      </div>
    </div>
  );
}

export default function ProducerCastingEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [producerProfileId, setProducerProfileId] = useState(null);
  const [castingStatus, setCastingStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [form, setForm] = useState({
    title: '',
    genre: '',
    platform: '',
    synopsis: '',
    shootStartYear: '', shootStartMonth: '',
    shootEndYear: '', shootEndMonth: '',
    shootLocationTbd: false,
    shootLocation: '',
    shootScheduleTbd: false,
    director: '',
    applyDeadline: '',
    requirements: '',
    notes: '',
  });
  const [roles, setRoles] = useState([defaultRole()]);
  const [stages, setStages] = useState([]);
  const [checklist, setChecklist] = useState({ complete: false, accurate: false, legal: false, rights: false });

  useEffect(() => {
    getCastingDetail(id).then(casting => {
      if (casting) {
        setProducerProfileId(casting.producerProfileId ?? null);
        setCastingStatus(casting.status ?? null);
        setForm({
          title: casting.title || '',
          genre: casting.projectType || '',
          platform: casting.category || '',
          synopsis: casting.synopsis || '',
          shootStartYear: casting.shootStartDate ? casting.shootStartDate.slice(0, 4) : '',
          shootStartMonth: casting.shootStartDate ? String(parseInt(casting.shootStartDate.slice(5, 7))) : '',
          shootEndYear: casting.shootEndDate ? casting.shootEndDate.slice(0, 4) : '',
          shootEndMonth: casting.shootEndDate ? String(parseInt(casting.shootEndDate.slice(5, 7))) : '',
          shootLocationTbd: !casting.regionProvince,
          // regionCity가 있으면 city를, 없으면 province를 사용 (LocationPicker와 동일 방식)
          shootLocation: casting.regionCity || casting.regionProvince || '',
          // 서버에는 '미정' 표시가 따로 없다. 날짜가 비어 있으면 미정으로 저장된 것이다
          // (지역의 shootLocationTbd와 같은 방식). false로 못박으면 미정으로 저장한
          // 공고를 다시 열었을 때 토글이 꺼진 채 날짜만 비어 보인다.
          shootScheduleTbd: !casting.shootStartDate && !casting.shootEndDate,
          director: casting.directorName || '',
          applyDeadline: casting.applicationDeadline || '',
          requirements: casting.requirements || '',
          notes: casting.additionalInfo || '',
        });

        // 역할 매핑 (roleId 보존)
        if (casting.roles && casting.roles.length > 0) {
          setRoles(casting.roles.map(r => ({
            roleId: r.id ?? null,
            name: r.roleName || '',
            type: r.roleType || '주연',
            gender: GENDER_API_TO_FORM[r.gender] || '전체',
            ageMin: r.ageMin ?? '',
            ageMax: r.ageMax ?? '',
            pay: r.pay || '',
            desc: r.characterDesc || '',
          })));
        }

        // 공고 단계 매핑
        const apiStageNames = (casting.stages || [])
          .sort((a, b) => a.stageNumber - b.stageNumber)
          .map(s => s.description || '');

        const builtStages = DEFAULT_STAGE_NAMES.map((name, i) => ({
          id: i + 1,
          name,
          active: apiStageNames.includes(name),
          editing: false,
        }));

        // 기본 단계에 없는 커스텀 단계 추가
        apiStageNames.forEach((name, idx) => {
          if (!DEFAULT_STAGE_NAMES.includes(name)) {
            builtStages.splice(builtStages.length - 1, 0, {
              id: Date.now() + idx,
              name,
              active: true,
              editing: false,
            });
          }
        });

        setStages(builtStages);
      }
      setIsLoading(false);
    });
  }, [id]);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const updateRole = (i, key, value) => setRoles(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: value } : r));
  const addRole = () => setRoles(prev => [...prev, defaultRole()]);
  const removeRole = (i) => setRoles(prev => prev.filter((_, idx) => idx !== i));

  const toggleStage = (sid) => setStages(prev => prev.map(s => s.id === sid ? { ...s, active: !s.active } : s));
  const startEditStage = (sid) => setStages(prev => prev.map(s => s.id === sid ? { ...s, editing: true } : s));
  const updateStageName = (sid, name) => setStages(prev => prev.map(s => s.id === sid ? { ...s, name } : s));
  const finishEditStage = (sid) => setStages(prev => prev.map(s => s.id === sid ? { ...s, editing: false } : s));
  const addStage = () => setStages(prev => {
    const newStage = { id: Date.now(), name: '새 단계', active: false, editing: true };
    return [...prev.slice(0, -1), newStage, prev[prev.length - 1]];
  });
  const deleteStage = (sid) => setStages(prev => {
    if (prev.length <= 1) return prev;
    const first = prev[0];
    const last = prev[prev.length - 1];
    if (sid === first.id || sid === last.id) return prev;
    return prev.filter(s => s.id !== sid);
  });

  const handleSave = async (isDraft) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const toLocalDate = (year, month) => {
        if (!year || !month) return undefined;
        return `${year}-${String(month).padStart(2, '0')}-01`;
      };

      const payload = {
        producerProfileId,
        title: form.title,
        projectType: form.genre,
        category: form.platform,
        synopsis: form.synopsis,
        requirements: form.requirements,
        additionalInfo: form.notes,
        directorName: form.director,
        castingDirectorName: '',
        referenceLink: '',
        regionProvince: form.shootLocationTbd ? '' : (() => {
          const loc = form.shootLocation;
          if (!loc) return '';
          const r = REGION_DATA.find(r => r.cities.includes(loc));
          return r ? (r.province === '광역시' ? loc : r.province) : loc;
        })(),
        regionCity: form.shootLocationTbd ? '' : (() => {
          const loc = form.shootLocation;
          if (!loc) return '';
          const r = REGION_DATA.find(r => r.cities.includes(loc));
          return r ? loc : '';
        })(),
        shootStartDate: form.shootScheduleTbd ? undefined : toLocalDate(form.shootStartYear, form.shootStartMonth),
        shootEndDate: form.shootScheduleTbd ? undefined : toLocalDate(form.shootEndYear, form.shootEndMonth),
        applicationDeadline: form.applyDeadline,
        auditionStageDescriptions: stages.filter(s => s.active).map(s => s.name),
        roles: roles.map((r, i) => ({
          // 기존 역할 식별자 — 백엔드 upsert가 이 값으로 갱신/신규를 구분한다.
          // 빠지면 모든 역할이 신규로 판정돼 삭제 후 재생성되고, 지원자-역할 연결이 끊긴다.
          roleId: r.roleId ?? undefined,
          roleName: r.name,
          roleType: r.type,
          gender: r.gender === '전체' ? 'ANY' : r.gender === '남성' ? 'MALE' : 'FEMALE',
          ageMin: r.ageMin ? Number(r.ageMin) : null,
          ageMax: r.ageMax ? Number(r.ageMax) : null,
          pay: r.pay || undefined,
          characterDesc: r.desc,
          sortOrder: i + 1,
        })),
      };
      await updateCasting(id, payload);
      changeCastingStatus(id, isDraft ? 'DRAFT' : 'OPEN').catch(() => {});
      navigate('/producer/projects');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.message || err.response?.data || err.message || '';
      console.error('[Edit] updateCasting error:', status, detail);
      alert(`저장 중 오류가 발생했습니다. (${status || 'network'}) ${detail ? '\n' + JSON.stringify(detail) : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  const allChecked = Object.values(checklist).every(Boolean);
  const activeStages = stages.filter(s => s.active);

  if (isLoading) {
    return (
      <ProducerContentContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <LoadingSpinner size="lg" />
        </div>
      </ProducerContentContainer>
    );
  }

  return (
    <ProducerContentContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
          <div>
            <h1 style={{ ...t('36px', 600, C.bk), margin: '0 0 8px 0' }}>공고 수정</h1>
            <span style={t('16px', 400, C.sub)}>공고 내용을 수정하세요.</span>
          </div>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', ...t('15px', 500, C.sub) }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            뒤로
          </button>
        </div>

        {/* 공고 기본 정보 */}
        <SectionCard title="공고 기본 정보">
          <FormRow label="공고 제목" required>
            <input value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="예: 넷플릭스 오리지날 드라마 배우 모집" style={inputStyle} />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FormRow label="장르" required>
              <select value={form.genre} onChange={e => updateForm('genre', e.target.value)} style={inputStyle}>
                <option value="">선택하세요</option>
                {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </FormRow>
            <FormRow label="방영 플랫폼">
              <select value={form.platform} onChange={e => updateForm('platform', e.target.value)} style={inputStyle}>
                <option value="">선택하세요</option>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormRow>
          </div>
          <FormRow label="소개" required>
            <textarea value={form.synopsis} onChange={e => updateForm('synopsis', e.target.value)} placeholder="작품의 줄거리와 핵심 내용을 작성해 주세요." style={{ ...textareaStyle, minHeight: '120px' }} />
          </FormRow>
        </SectionCard>

        {/* 촬영 정보 */}
        <SectionCard title="촬영 정보">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
            <FormRow
              label="촬영 시작"
              required
              extra={<TbdButton active={form.shootScheduleTbd} onClick={() => updateForm('shootScheduleTbd', !form.shootScheduleTbd)} />}
            >
              {!form.shootScheduleTbd && (
                <YearMonthPicker
                  year={form.shootStartYear} month={form.shootStartMonth}
                  onYearChange={v => updateForm('shootStartYear', v)}
                  onMonthChange={v => updateForm('shootStartMonth', v)}
                />
              )}
              {form.shootScheduleTbd && <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 일정 미정</div>}
            </FormRow>
            <FormRow label="촬영 종료" required>
              {!form.shootScheduleTbd && (
                <YearMonthPicker
                  year={form.shootEndYear} month={form.shootEndMonth}
                  onYearChange={v => updateForm('shootEndYear', v)}
                  onMonthChange={v => updateForm('shootEndMonth', v)}
                />
              )}
              {form.shootScheduleTbd && <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 일정 미정</div>}
            </FormRow>
            <FormRow
              label="촬영 장소"
              required
              extra={<TbdButton active={form.shootLocationTbd} onClick={() => { updateForm('shootLocationTbd', !form.shootLocationTbd); if (!form.shootLocationTbd) updateForm('shootLocation', ''); }} />}
            >
              {!form.shootLocationTbd
                ? <LocationPicker value={form.shootLocation} onChange={v => updateForm('shootLocation', v)} />
                : <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 장소 미정</div>
              }
            </FormRow>
            <FormRow label="감독">
              <input value={form.director} onChange={e => updateForm('director', e.target.value)} placeholder="감독명" style={inputStyle} />
            </FormRow>
            <div style={{ gridColumn: '1 / -1' }}>
            </div>
          </div>
        </SectionCard>

        {/* 캐스팅 역할 */}
        <SectionCard title="캐스팅 역할">
          {castingStatus === 'DRAFT' ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                {roles.map((role, i) => (
                  <RoleCard key={i} role={role} index={i} onChange={(key, val) => updateRole(i, key, val)} onRemove={() => removeRole(i)} />
                ))}
              </div>
              <button
                onClick={addRole}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '12px', border: `1px dashed ${C.g300}`, background: C.white, cursor: 'pointer', ...t('15px', 500, C.sub), width: '100%', justifyContent: 'center' }}
              >
                <span style={{ fontSize: '18px' }}>+</span> 역할 추가
              </button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderRadius: '10px', background: '#FFF8E1', border: '1px solid #FFE082', marginBottom: '16px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="1.5"/>
                  <path d="M12 8v4M12 16h.01" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span style={t('13px', 400, '#92400E')}>모집 중인 공고의 역할은 수정할 수 없습니다. 기존 지원자의 역할 정보 보호를 위해 공고 등록 후에는 역할 정보가 고정됩니다.</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {roles.map((role, i) => (
                  <RoleCardReadOnly key={i} role={role} index={i} />
                ))}
              </div>
            </>
          )}
        </SectionCard>

        {/* 공고 절차 */}
        <SectionCard title="공고 절차">
          <div style={{ marginBottom: '16px', ...t('14px', 400, C.sub) }}>진행할 공고 단계를 선택하고, 이름을 수정하거나 새로 추가할 수 있습니다.</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
            {stages.map(stage => (
              <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {stage.editing ? (
                  <input
                    autoFocus
                    value={stage.name}
                    onChange={e => updateStageName(stage.id, e.target.value)}
                    onBlur={() => finishEditStage(stage.id)}
                    onKeyDown={e => e.key === 'Enter' && finishEditStage(stage.id)}
                    style={{ padding: '8px 12px', borderRadius: '100px', border: `1px solid ${C.bk}`, fontFamily: ff, fontSize: '14px', outline: 'none', width: '120px' }}
                  />
                ) : (
                  <button
                    onClick={() => toggleStage(stage.id)}
                    style={{ padding: '10px 18px', borderRadius: '100px', border: `1px solid ${stage.active ? C.bk : C.g200}`, background: stage.active ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, stage.active ? C.white : C.bk) }}
                  >
                    {stage.name}
                  </button>
                )}
                <button
                  onClick={() => startEditStage(stage.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  title="이름 수정"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                {stages.length > 1 && stage.id !== stages[0]?.id && stage.id !== stages[stages.length - 1]?.id && (
                  <button
                    onClick={() => deleteStage(stage.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    title="단계 삭제"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addStage}
              style={{ padding: '10px 14px', borderRadius: '100px', border: `1px dashed ${C.g300}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.sub), display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ fontSize: '16px' }}>+</span> 단계 추가
            </button>
          </div>
          {activeStages.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px 20px', background: C.bg, borderRadius: '12px', flexWrap: 'wrap' }}>
              {activeStages.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.bk, display: 'flex', alignItems: 'center', justifyContent: 'center', ...t('13px', 700, C.white) }}>{i + 1}</div>
                    <span style={t('12px', 500, C.bk)}>{s.name}</span>
                  </div>
                  {i < activeStages.length - 1 && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '16px' }}><path d="M9 18l6-6-6-6" stroke={C.g300} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* 지원 정보 */}
        <SectionCard title="지원 정보">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FormRow label="지원 마감일" required>
              <input type="date" value={form.applyDeadline} onChange={e => updateForm('applyDeadline', e.target.value)} style={inputStyle} />
            </FormRow>
          </div>
          <FormRow label="지원요건">
            <textarea
              value={form.requirements}
              onChange={e => updateForm('requirements', e.target.value)}
              placeholder={`지원 시 필요한 요건을 작성해 주세요.\n예시:\n- 만 20세 이상\n- 기본 연기 경험자 우대`}
              style={{ ...textareaStyle, minHeight: '140px' }}
            />
          </FormRow>
        </SectionCard>

        {/* 추가 정보 */}
        <SectionCard title="추가 정보">
          <FormRow label="기타 안내사항">
            <textarea value={form.notes} onChange={e => updateForm('notes', e.target.value)} placeholder="기타 안내 사항이 있으면 작성해 주세요." style={textareaStyle} />
          </FormRow>
        </SectionCard>

        {/* 검수 체크리스트 */}
        <SectionCard title="검수 체크리스트">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[
              { key: 'complete', label: '공고의 모든 필수 항목이 작성되었습니다.' },
              { key: 'accurate', label: '작품 정보와 캐스팅 조건이 정확합니다.' },
              { key: 'legal', label: '관련 법규 및 노동 기준을 준수합니다.' },
              { key: 'rights', label: '초상권 및 저작권 관련 사항을 확인했습니다.' },
            ].map(item => (
              <div key={item.key} onClick={() => setChecklist(prev => ({ ...prev, [item.key]: !prev[item.key] }))} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <div
                  style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${checklist[item.key] ? C.bk : C.g200}`, background: checklist[item.key] ? C.bk : C.white, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {checklist[item.key] && <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={C.white} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <span style={t('14px', 400, C.bk)}>{item.label}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Bottom action bar */}
        <div style={{ position: 'sticky', bottom: 0, background: C.white, borderTop: `1px solid ${C.g200}`, padding: '20px 40px', margin: '0 -40px -60px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button onClick={() => navigate(-1)} style={{ padding: '14px 28px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('15px', 600, C.bk) }}>
            취소
          </button>
          <button onClick={() => handleSave(true)} disabled={submitting} style={{ padding: '14px 28px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: submitting ? 'default' : 'pointer', ...t('15px', 600, C.bk), opacity: submitting ? 0.5 : 1 }}>
            {submitting ? '저장 중...' : '임시 저장'}
          </button>
          <button
            onClick={() => allChecked ? handleSave(false) : alert('검수 체크리스트를 모두 확인해 주세요.')}
            disabled={submitting}
            style={{ padding: '14px 28px', borderRadius: '12px', border: 'none', background: allChecked && !submitting ? C.bk : C.g200, cursor: allChecked && !submitting ? 'pointer' : 'default', ...t('15px', 600, allChecked && !submitting ? C.white : C.sub) }}
          >
            {submitting ? '저장 중...' : '수정 완료'}
          </button>
        </div>
      </ProducerContentContainer>
  );
}
